import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { PlantScope, usePlantScope } from '../../hooks/usePlantScope';
import { getWorkingPlantId, setWorkingPlantId } from '../../lib/plantScope';
import { Field, inputClass, inputErrorClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';

// The plant a new document starts with: the only one there is, or the one being worked in
export const initialPlantId = (scope: PlantScope): string => {
  if (scope.defaultPlantId) return scope.defaultPlantId;
  const working = getWorkingPlantId();
  return scope.plants.some((plant) => plant.id === working) ? working : '';
};

// true when the form may not be sent yet because no plant is chosen
export const plantMissing = (scope: PlantScope, plantId: string): boolean => scope.required && scope.plants.length > 1 && !plantId;

// What goes into the request
export const plantPayload = (plantId: string): { plantId?: string } => (plantId ? { plantId } : {});

// Keeps the plant of a create form: starts from the default and follows it once the plants have loaded
export const usePlantChoice = () => {
  const { scope } = usePlantScope();
  const [plantId, setPlantId] = useState('');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setPlantId(initialPlantId(scope));
  }, [scope, touched]);
  return {
    scope,
    plantId,
    setPlantId: (value: string) => {
      setTouched(true);
      setPlantId(value);
    },
    missing: plantMissing(scope, plantId),
  };
};

// The plant of a new document. Shows nothing while no plant is set up, the name when there is
// only one to choose from, and a list otherwise.
export const PlantField: React.FC<{ id: string; scope: PlantScope; value: string; onChange: (plantId: string) => void; showError?: boolean; className?: string }> = ({ id, scope, value, onChange, showError, className }) => {
  if (!scope.required || scope.plants.length === 0) return null;
  if (scope.plants.length === 1) {
    return (
      <Field label="Plant" htmlFor={id} className={className}>
        <input id={id} type="text" readOnly value={`${scope.plants[0].name} (${scope.plants[0].code})`} className={cn(inputClass, 'bg-gray-50 text-gray-600')} />
      </Field>
    );
  }
  const error = showError && !value ? 'Choose the plant this document belongs to' : undefined;
  return (
    <Field label="Plant" htmlFor={id} required error={error} className={className}>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className={cn(inputClass, error && inputErrorClass)}>
        <option value="">Choose a plant</option>
        {scope.plants.map((plant) => (<option key={plant.id} value={plant.id}>{plant.name} ({plant.code})</option>))}
      </select>
    </Field>
  );
};

// The name of a document's plant, where the user can see more than one
export const PlantTag: React.FC<{ plantId?: string | null; className?: string }> = ({ plantId, className }) => {
  const { scope } = usePlantScope();
  if (!plantId || scope.plants.length < 2) return null;
  const plant = scope.plants.find((item) => item.id === plantId);
  if (!plant) return null;
  return <span className={cn('block text-xs text-gray-500', className)} data-testid="plant-tag">{plant.name}</span>;
};

// In the header: narrows the sales, purchase and work order lists to one plant. Only there when
// the user has more than one plant to look at.
export const PlantSwitcher: React.FC = () => {
  const { scope } = usePlantScope();
  const queryClient = useQueryClient();
  const [plantId, setPlantId] = useState(getWorkingPlantId());

  // A plant that was taken away from the user, or made inactive, is no longer a choice
  useEffect(() => {
    if (plantId && scope.plants.length > 0 && !scope.plants.some((plant) => plant.id === plantId)) {
      setWorkingPlantId('');
      setPlantId('');
      queryClient.invalidateQueries();
    }
  }, [scope, plantId, queryClient]);

  if (scope.plants.length < 2) return null;

  const choose = (value: string) => {
    setWorkingPlantId(value);
    setPlantId(value);
    // Every list on screen is fetched again for the new plant
    queryClient.invalidateQueries();
  };

  return (
    <label className="flex items-center gap-2 text-sm text-gray-600">
      <BuildingOffice2Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      <span className="sr-only">Plant</span>
      <select value={plantId} onChange={(event) => choose(event.target.value)} aria-label="Plant" className="rounded-md border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
        <option value="">{scope.restricted ? 'All my plants' : 'All plants'}</option>
        {scope.plants.map((plant) => (<option key={plant.id} value={plant.id}>{plant.name}</option>))}
      </select>
    </label>
  );
};
